import type {
  CompositionRecord,
  CallDeclaration,
  ExactVersion,
  Location,
  Manifest,
  PrepareRequest,
  StructuralPath,
  ProofKind,
  Requirement,
} from '@/types/executionCompositions.ts';

export const proofKindLabel: Record<ProofKind, string> = {
  component: 'Component behavior',
  parent_route: 'Complete workflow',
  final_artifact: 'Final output',
  comparison: 'Comparison with a baseline',
};
export const outcomeLabel: Record<Requirement['outcome'], string> = {
  produced: 'Produced artifact',
  unavailable: 'No output expected',
  non_artifact: 'Structured result',
};

/** Compare included form intent, never infer evidence or server compatibility. */
export function proposalChanged(request: PrepareRequest, basis?: PrepareRequest) {
  if (!basis) return true;
  return JSON.stringify(request) !== JSON.stringify(basis);
}

export const pathLabel = (path: StructuralPath) =>
  path.map((item) => `${item.subflow_id ? `${item.subflow_id}/` : ''}${item.step_id}`).join(' / ');
const rootKey = (location: Location) =>
  JSON.stringify([
    location.root.automation_id,
    location.root.automation_version_id,
    location.root.trigger_step_id,
  ]);
const structuralKey = (path: StructuralPath) =>
  JSON.stringify(path.map((segment) => [segment.step_id, segment.subflow_id ?? null]));
export const locationKey = (location: Location) =>
  JSON.stringify([rootKey(location), location.call_chain.map(structuralKey)]);
export type InheritedCall = { implementation: ExactVersion; call: CallDeclaration };
export function matchingInheritedCall(
  previous: InheritedCall | undefined,
  call: Pick<CallDeclaration, 'binding_mode' | 'target_automation_id' | 'target_trigger_step_id'>,
) {
  return previous?.call.binding_mode === call.binding_mode &&
    previous.call.target_automation_id === call.target_automation_id &&
    previous.call.target_trigger_step_id === call.target_trigger_step_id
    ? previous.implementation
    : undefined;
}
/** Form intent only; the backend resolves and checks the compiled bindings. */
export function chooseDependency(
  request: PrepareRequest,
  location: Location,
  implementation: ExactVersion | null,
  previous?: ExactVersion,
): PrepareRequest {
  const exact = (version: ExactVersion): ExactVersion => ({
    automation_id: version.automation_id,
    automation_version_id: version.automation_version_id,
  });
  return {
    ...request,
    requirements: request.requirements.filter(
      (item) => !locationWithin(item.evidence_location, location),
    ),
    selections: [
      ...request.selections.filter((item) => !locationWithin(item.location, location)),
      ...(previous &&
      implementation &&
      previous.automation_version_id !== implementation.automation_version_id
        ? [{ location, previous: exact(previous), replacement: exact(implementation) }]
        : []),
    ],
    initial_selections: [
      ...request.initial_selections.filter((item) => !locationWithin(item.location, location)),
      ...(!previous && implementation ? [{ location, implementation: exact(implementation) }] : []),
    ],
  };
}
export const locationLabel = (location: Location) =>
  `${location.root.automation_id} · ${location.root.trigger_step_id}${location.call_chain.length ? ` → ${location.call_chain.map(pathLabel).join(' → ')}` : ''}`;
export function locationWithin(candidate: Location, ancestor: Location) {
  return (
    rootKey(candidate) === rootKey(ancestor) &&
    candidate.call_chain.length >= ancestor.call_chain.length &&
    ancestor.call_chain.every(
      (path, index) => structuralKey(path) === structuralKey(candidate.call_chain[index]),
    )
  );
}

/** Traverse the server's retained graph for presentation only. Never select implementations. */
export function bindingOccurrences(
  manifest: Manifest,
): { location: Location; bindingId: string; edgeIndex: number }[] {
  const result: ReturnType<typeof bindingOccurrences> = [];
  function visit(location: Location, bindingId: string, ancestors: Set<string>) {
    if (!manifest.nodes.some((node) => node.binding_id === bindingId))
      throw new Error('Composition contains an unavailable binding.');
    if (ancestors.has(bindingId) || location.call_chain.length > 64 || result.length >= 5000)
      throw new Error('Composition inspection exceeded its structural bounds.');
    const next = new Set([...ancestors, bindingId]);
    manifest.edges.forEach((edge, edgeIndex) => {
      if (edge.source_binding_id !== bindingId) return;
      const child = { root: location.root, call_chain: [...location.call_chain, edge.occurrence] };
      if (!manifest.nodes.some((node) => node.binding_id === edge.target_binding_id))
        throw new Error('Composition contains an unavailable binding.');
      result.push({ location: child, bindingId: edge.target_binding_id, edgeIndex });
      visit(child, edge.target_binding_id, next);
    });
  }
  manifest.roots.forEach((root) =>
    visit(
      {
        root: {
          automation_id: root.automation_id,
          automation_version_id: root.automation_version_id,
          trigger_step_id: root.trigger_step_id,
        },
        call_chain: [],
      },
      root.binding_id,
      new Set(),
    ),
  );
  return result;
}
export function preparationFromRecord(record: CompositionRecord): PrepareRequest {
  const manifest = record.candidate;
  const occurrences = bindingOccurrences(manifest);
  return {
    project_id: manifest.project_id,
    suite_id: manifest.suite?.suite_id ?? null,
    expected_suite_head_revision: manifest.suite?.head_revision ?? null,
    base: record.record,
    roots: manifest.roots.map(
      ({ automation_id, automation_version_id, trigger_step_id, role }) => ({
        automation_id,
        automation_version_id,
        trigger_step_id,
        role,
      }),
    ),
    selections: [],
    initial_selections: [],
    requirements: manifest.requirements.map((item) => {
      const root = manifest.roots.find(
        (root) =>
          root.automation_version_id === item.declaration.entrypoint.automation_version_id &&
          root.trigger_step_id === item.declaration.entrypoint.trigger_step_id,
      );
      const occurrence =
        root?.binding_id === item.evidence_binding_id
          ? null
          : occurrences.find(
              (entry) =>
                entry.bindingId === item.evidence_binding_id &&
                entry.location.root.automation_version_id ===
                  item.declaration.entrypoint.automation_version_id &&
                entry.location.root.trigger_step_id === item.declaration.entrypoint.trigger_step_id,
            );
      if (!root || (root.binding_id !== item.evidence_binding_id && !occurrence))
        throw new Error('Requirement has no accessible contextual location.');
      return {
        declaration: item.declaration,
        evidence_location: occurrence?.location ?? {
          root: {
            automation_id: root.automation_id,
            automation_version_id: root.automation_version_id,
            trigger_step_id: root.trigger_step_id,
          },
          call_chain: [],
        },
      };
    }),
  };
}
